import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  threadsListThreads,
  threadsGetOrCreateThread,
} from "../gen";
import type { ThreadResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { useUserDirectory, normalizeAgentType } from "../hooks/useUserDirectory";
import { useToast } from "../context/ToastContext";
import { FilterSelect, type FilterSelectOption } from "../components/common/FilterSelect";
import { ThreadInboxItem } from "../components/thread/ThreadInboxItem";
import { ThreadDetail } from "./ThreadDetail";
import { NewConversationModal } from "../components/thread/NewConversationModal";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { BookmarkPlus, Star } from "lucide-react";
import { formatChannelName } from "../components/thread/threadUtils";
import { useConversationViews, getColorOption } from "../context/ConversationViewsContext";
import { ViewModal } from "../components/views/ViewModal";

export function ThreadsWorkspace() {
  const { thread_id } = useParams<{ thread_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrgId } = useOrg();
  const { showToast } = useToast();

  const [threads, setThreads] = useState<ThreadResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [agentFilter, setAgentFilter] = useFilterParam("agent", "all", ["agent_type"]);
  const [partyFilter, setPartyFilter] = useFilterParam("party", "");
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [quickFilter, setQuickFilter] = useFilterParam("quick_filter", "all", ["eval", "evaluation"]);
  const includeSimulation = statusFilter === "simulation" || statusFilter === "all";
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const agentOptions = useAgentOptions();

  const STATUS_OPTIONS: FilterSelectOption[] = [
    { value: "all", label: "All Conversations" },
    { value: "prod_only", label: "Production Only" },
    { value: "exclude_simulation", label: "Exclude Sims" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "simulation", label: "Simulation" },
    { value: "archived", label: "Archived" },
  ];

  const EVAL_OPTIONS: FilterSelectOption[] = [
    { value: "low_score", label: "Flagged" },
    { value: "high_score", label: "High Score" },
    { value: "unreviewed", label: "Unreviewed" },
  ];

  // Saved Views Integration
  const {
    activeView,
    defaultView,
    applyView,
    clearActiveView,
    createView,
    updateView,
  } = useConversationViews();
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [savingView, setSavingView] = useState(false);

  // Auto-apply default view when visiting /threads directly without query params
  useEffect(() => {
    if (!location.search && defaultView && location.pathname === "/threads") {
      applyView(defaultView);
    }
  }, [location.search, location.pathname, defaultView, applyView]);

  const isFiltersModified = useMemo(() => {
    if (!activeView) return false;
    const f = (activeView.filters || {}) as Record<string, any>;
    const viewAgent = f.agent || "all";
    const viewParty = f.party || "";
    const viewStatus = f.status || "all";
    const viewQuickFilter = f.quick_filter || f.evaluation || "all";
    const viewQ = f.q || "";

    return (
      agentFilter !== viewAgent ||
      partyFilter !== viewParty ||
      statusFilter !== viewStatus ||
      quickFilter !== viewQuickFilter ||
      searchQuery !== viewQ
    );
  }, [activeView, agentFilter, partyFilter, statusFilter, quickFilter, searchQuery]);

  const hasAnyFilterActive =
    agentFilter !== "all" ||
    partyFilter.trim() !== "" ||
    statusFilter !== "all" ||
    quickFilter !== "all" ||
    searchQuery.trim() !== "";

  const handleQuickUpdateView = async () => {
    if (!activeView) return;
    const f: Record<string, any> = {};
    if (agentFilter !== "all") f.agent = agentFilter;
    if (partyFilter.trim()) f.party = partyFilter.trim();
    if (statusFilter !== "all") f.status = statusFilter;
    if (quickFilter !== "all") f.quick_filter = quickFilter;
    if (searchQuery.trim()) f.q = searchQuery.trim();

    try {
      await updateView(activeView.id, { filters: f });
    } catch {
      // toast handled
    }
  };

  // New Conversation Modal
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("biblioteca");
  const [creatingThread, setCreatingThread] = useState(false);

  const handleCreateThread = async () => {
    setCreatingThread(true);
    try {
      const source_thread_ts = crypto.randomUUID();
      const res = await threadsGetOrCreateThread({
        agent_type: selectedAgent,
        source_channel_id: "knowledge-web",
        source_thread_ts: source_thread_ts,
        metadata: { source: "knowledge-web" },
      });
      const newThread = (res as any).data;
      setIsNewModalOpen(false);
      showToast("Conversation thread created", { type: "success" });
      navigate(`/threads/${newThread.id}${location.search}`);
      loadThreads();
    } catch (err: any) {
      showToast("Failed to create conversation thread: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setCreatingThread(false);
    }
  };

  const loadThreads = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setError(null);
    }
    try {
      const params: any = {
        include_simulation: includeSimulation || statusFilter === "simulation",
        include_test: statusFilter !== "prod_only",
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (statusFilter === "prod_only") {
        params.prod_only = true;
      }
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (statusFilter !== "all" && statusFilter !== "prod_only") params.status = statusFilter;
      if (partyFilter.trim()) params.party = partyFilter.trim();
      if (quickFilter !== "all") params.quick_filter = quickFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await threadsListThreads(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setThreads(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setThreads(data);
        setTotal(data.length);
      } else {
        setThreads([]);
        setTotal(0);
      }
    } catch (err: any) {
      if (!isBackground) {
        setError(err?.message || "Failed to load conversational threads");
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
  }, [agentFilter, partyFilter, statusFilter, quickFilter, includeSimulation, searchQuery]);

  useEffect(() => {
    loadThreads(false);

    // Auto-refresh thread list periodically to reflect newly incoming threads and turns
    const interval = setInterval(() => {
      loadThreads(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [agentFilter, partyFilter, statusFilter, quickFilter, includeSimulation, searchQuery, page, pageSize, activeOrgId]);

  const filteredThreads = useMemo(() => {
    let result = threads;
    if (statusFilter === "exclude_simulation") {
      result = result.filter(
        (t) => t.status !== "simulation" && !(t.metadata as any)?.is_simulation
      );
    }
    return [...result].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at).getTime() -
        new Date(a.last_message_at || a.created_at).getTime()
    );
  }, [threads, statusFilter]);
  const { getAgentDisplayName } = useUserDirectory();
  const hasSelectedItem = Boolean(thread_id && filteredThreads.some((t) => t.id === thread_id));

  const getAgentBadge = (agentType: string) => {
    const norm = normalizeAgentType(agentType);
    const customName = getAgentDisplayName(norm);
    switch (norm) {
      case "sales":
        return {
          label: customName || "Quotes",
          role: "Sales Concierge",
          badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800",
        };
      case "biblioteca":
        return {
          label: customName || "Analytics",
          role: "Analytics Lead",
          badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-400 dark:border-indigo-800",
        };
      case "frontdesk":
        return {
          label: customName || "Sofia",
          role: "Front Desk Concierge",
          badgeClass: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800",
        };
      case "feedback":
        return {
          label: customName || "Feedback",
          role: "Feedback & Curation",
          badgeClass: "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/60 dark:text-cyan-400 dark:border-cyan-800",
        };
      case "email_drafting":
        return {
          label: customName || "Email Drafter",
          role: "Gmail Inbound Concierge",
          badgeClass: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/60 dark:text-violet-400 dark:border-violet-800",
        };
      default:
        return {
          label: customName || norm || "Universal Agent",
          badgeClass: "bg-stone-100 text-stone-700 border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700",
        };
    }
  };

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Conversations Inbox Pane (320px–380px)      */}
      {/* ============================================================== */}
      <div
        className={`${
          thread_id ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all isolate`}
      >
        {/* Pane Header: Title & Quick Controls */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Conversations
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredThreads.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => loadThreads(false)}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh conversations list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setIsNewModalOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New</span>
            </button>
          </div>
        </div>

        {/* Active View Banner (if selected) */}
        {activeView && (
          <div
            className={`px-3 py-2 border-b border-stone-200/80 dark:border-stone-800 bg-stone-100/70 dark:bg-stone-800/40 border-l-[3.5px] ${
              getColorOption(activeView.color).borderClass
            } flex items-center justify-between gap-2 shrink-0`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-bold text-stone-900 dark:text-stone-100 truncate">
                {activeView.name}
              </span>
              <span className="text-[10px] text-stone-400 dark:text-stone-500 font-medium">
                {activeView.is_global ? "Global" : "Private"}
              </span>
              {activeView.is_default && (
                <span className="px-1.5 py-0.2 rounded-md bg-amber-50 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[9px] font-semibold flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-500" />
                  <span>Default</span>
                </span>
              )}
              {isFiltersModified && (
                <span className="px-1.5 py-0.2 rounded-md bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800 text-[9px] font-semibold">
                  Modified
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {isFiltersModified && (
                <button
                  type="button"
                  onClick={handleQuickUpdateView}
                  className="px-2 py-0.5 rounded text-[10px] font-semibold bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:bg-stone-800 dark:hover:bg-white transition"
                  title="Save modified filters to this view"
                >
                  Save
                </button>
              )}
              <button
                type="button"
                onClick={clearActiveView}
                className="p-1 rounded text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-200/60 dark:hover:bg-stone-700 transition"
                title="Exit active view"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Search Input Bar & Filters */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Horizontal Dropdowns: Agent -> Status -> Evaluation */}
          <div className="grid grid-cols-3 gap-1.5">
            <FilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              options={agentOptions}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
            />
            <FilterSelect
              label="Evaluation"
              value={quickFilter}
              onChange={setQuickFilter}
              options={EVAL_OPTIONS}
            />
          </div>

          {/* Active Party Tag (if filtered by party) */}
          {partyFilter.trim() && (
            <div className="flex items-center justify-between px-2 py-1 bg-stone-100 dark:bg-stone-800/60 rounded-md border border-stone-200 dark:border-stone-700/60 text-[11px]">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-stone-500 dark:text-stone-400 font-medium">Party:</span>
                <span className="font-semibold text-stone-900 dark:text-stone-100 truncate">
                  {partyFilter}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPartyFilter("")}
                className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 ml-1"
                title="Clear party filter"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Save as View Action */}
          {hasAnyFilterActive && (!activeView || isFiltersModified) && (
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-stone-400 dark:text-stone-500">
                Filtered view active
              </span>
              <button
                type="button"
                onClick={() => setIsSaveViewModalOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white transition"
              >
                <BookmarkPlus className="w-3 h-3 text-stone-500 dark:text-stone-400" />
                <span>Save as View</span>
              </button>
            </div>
          )}
        </div>

        {/* Scrollable Inbox List */}
        <ScrollableColumnContainer>
          {loading && threads.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading conversations...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <MessageSquare className="w-7 h-7 text-stone-300 dark:text-stone-600 mx-auto" />
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                No conversations found
              </div>
              <p className="text-[11px] text-stone-400 dark:text-stone-500">
                Adjust search queries or filters to see incoming turns.
              </p>
            </div>
          ) : (
            filteredThreads.map((t) => (
              <ThreadInboxItem
                key={t.id}
                thread={t}
                isSelected={t.id === thread_id}
                isDimmed={hasSelectedItem && t.id !== thread_id}
                onClick={() => navigate(`/threads/${t.id}${location.search}`)}
                getAgentBadge={getAgentBadge}
              />
            ))
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail / Workspace Pane (Flex-1)                      */}
      {/* ============================================================== */}
      <div
        className={`${
          thread_id ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-hidden flex-col bg-stone-50 dark:bg-stone-950`}
      >
        {thread_id ? (
          <ThreadDetail key={thread_id} />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <MessageSquare className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1 max-w-sm">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Select a Conversation
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Pick a conversation thread from the left column to review message exchanges,
                inspect rule governance, and add margin annotations.
              </p>
            </div>
            {filteredThreads.length > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/threads/${filteredThreads[0].id}${location.search}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition shadow-2xs"
              >
                <span>View Latest ({formatChannelName(filteredThreads[0].source_channel_id, filteredThreads[0].metadata) || "Thread"})</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        selectedAgent={selectedAgent}
        onSelectAgent={setSelectedAgent}
        onSubmit={handleCreateThread}
        creating={creatingThread}
      />

      {/* Save View Modal */}
      <ViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSubmit={async (data) => {
          setSavingView(true);
          try {
            await createView(data);
            setIsSaveViewModalOpen(false);
          } finally {
            setSavingView(false);
          }
        }}
        defaultFilters={{
          agent: agentFilter,
          party: partyFilter,
          status: statusFilter,
          quick_filter: quickFilter,
          q: searchQuery,
        }}
        isSubmitting={savingView}
      />
    </div>
  );
}
